import { mapMessage } from '../../mappers';
import { fetchWebHistoryMessages, TWebHistoryMessagesResponse } from '../../repositories/chats/v3/web/history/messages';
import { IApplicationContext } from '../../types/applicationContext';
import { EExceptionDomain } from '../../types/exceptionDomains';
import { TMessagesResponseError } from '../../types/messages';
import { EMessagesLoadReason } from '../../types/messagesLoadReason';
import { IPageStateLogData } from '../../utils/getPageStateLogData';
import { getTelemetryByPlatform } from '../../utils/getTelemetryByPlatform';
import { getGenericLikeError } from '../../utils/logging/getGenericLikeError';
import { getResponseError } from '../../utils/logging/getResponseError';
import {
  DEFAULT_ERROR_MESSAGE,
  HISTORY_MESSAGES_REQUEST_LIMIT,
  MESSAGES_GET_TELEMETRY_EVENTS,
} from './constants';

interface IWebHistoryMessagesRequestParameters {
  chatId: string;
  /** Дата, после которой нужно получить сообщения  ISO-8601, пример: 2022-08-01T01:34:03.123456+00:00. **/
  createdAt?: string;
  rowVersionsHash?: string;
}

type TFetchWebHistoryMessagesParams = IWebHistoryMessagesRequestParameters & {
  cookies?: string;
  signal?: AbortSignal;
  /** Причина загрузки, задаёт набор событий телеметрии */
  loadReason?: EMessagesLoadReason;
  logData?: IPageStateLogData;
};

export async function fetchHistoryMessages(
  { logger, httpApi, telemetry, custom: { realDevice, hostType } }: IApplicationContext,
  {
    cookies,
    signal,
    loadReason = EMessagesLoadReason.Regular,
    logData,
    ...parameters
  }: TFetchWebHistoryMessagesParams,
) {
  const causedByReplyScroll = loadReason === EMessagesLoadReason.ReplyScroll;

  const startTime = Date.now();

  const messagesTelemetry = getTelemetryByPlatform({ telemetry, realDevice, hostType });

  const telemetryEvents = MESSAGES_GET_TELEMETRY_EVENTS[loadReason];

  messagesTelemetry.increment(telemetryEvents.init);

  try {
    const { response, statusCode }: TWebHistoryMessagesResponse = await fetchWebHistoryMessages({
      httpApi,
      parameters: {
        ...parameters,
        limit: HISTORY_MESSAGES_REQUEST_LIMIT,
      },
      config: {
        requestConfig: {
          headers: cookies ? [['Cookie', cookies]] : [],
          withCredentials: true,
        },
        signal,
      },
    });

    if (statusCode !== 200) {
      throw getResponseError({
        domain: EExceptionDomain.Messages,
        response,
        defaultMessage: DEFAULT_ERROR_MESSAGE,
        statusCode,
        details: { causedByReplyScroll },
      });
    }

    messagesTelemetry.increment(telemetryEvents.success);
    messagesTelemetry.timing(telemetryEvents.time, Date.now() - startTime);

    return {
      response: {
        ...response,
        messages: response.messages.map(mapMessage),
      },
      error: null,
    };
  } catch (error) {
    const exception = getGenericLikeError({
      error,
      domain: EExceptionDomain.Messages,
      message: DEFAULT_ERROR_MESSAGE,
    });

    const responseError: TMessagesResponseError = { message: exception.message };

    if (signal?.aborted) {
      return { response: null, error: responseError };
    }

    messagesTelemetry.increment(telemetryEvents.fail);

    logger.error(exception, {
      microserviceName: 'chats',
      pathApi: '/v3/web/history/messages/',
      parameters: JSON.stringify(parameters),
      causedByReplyScroll,
      ...logData,
    });

    return { response: null, error: responseError };
  }
}
